import {
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
  type RefObject,
} from "react";
import { normalizeTextHtml } from "../domain/sanitize";
import { getEditorStoreState } from "../domain/store";
import {
  isTextBlockEditing,
  isTextFrameChromeTarget,
  isEmailBuilderFieldMergeTagPopoverTarget,
  isInlineMergeTagAutocompleteTarget,
  isTextToolbarTarget,
} from "../domain/text-editing";
import type { TextBlock } from "../domain/types";
import {
  readEmailBuilderRichTextStoredHtml,
  setEmailBuilderRichTextCaretPoint,
} from "../editing/richText/emailBuilderRichTextSession";

type BlockTarget = {
  rowId: string;
  columnId: string;
  blockId: string;
};

type UseEditableTextEditSessionParams = {
  rowId: string;
  columnId: string;
  block: TextBlock;
  isEditing: boolean;
  target: BlockTarget;
  setSelection: (selection: { kind: "block" } & BlockTarget) => void;
  setTextEditingBlock: (target: BlockTarget | null) => void;
  updateBlock: (
    rowId: string,
    columnId: string,
    blockId: string,
    patch: Partial<TextBlock>,
  ) => void;
  setTextEditReentryGuard: (blockId: string | null) => void;
  cellRef: RefObject<HTMLTableCellElement | null>;
  exitingRef: MutableRefObject<boolean>;
  toolbarPressRef: MutableRefObject<boolean>;
};

function readInlineEditorElement(blockId: string): HTMLElement | null {
  return document.querySelector(`[data-email-builder-inline-text="${blockId}"]`);
}

function isInlineTextLinkPopoverTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(".email-builder-text-link-popover") !== null
  );
}

export function useEditableTextEditSession({
  rowId,
  columnId,
  block,
  isEditing,
  target,
  setSelection,
  setTextEditingBlock,
  updateBlock,
  setTextEditReentryGuard,
  cellRef,
  exitingRef,
  toolbarPressRef,
}: UseEditableTextEditSessionParams) {
  const exitEdit = useCallback(
    (setGuard: boolean) => {
      const editor = readInlineEditorElement(block.id);
      exitingRef.current = true;
      const html = readEmailBuilderRichTextStoredHtml(block.id);
      if (html !== null) {
        const normalized = normalizeTextHtml(html);
        if (normalized !== block.html) {
          updateBlock(rowId, columnId, block.id, { html: normalized });
        }
      }
      setTextEditingBlock(null);
      if (setGuard) {
        setTextEditReentryGuard(block.id);
      }
      editor?.blur();
      queueMicrotask(() => {
        exitingRef.current = false;
      });
    },
    [
      block.html,
      block.id,
      columnId,
      exitingRef,
      rowId,
      setTextEditReentryGuard,
      setTextEditingBlock,
      updateBlock,
    ],
  );

  const exitEditRef = useRef(exitEdit);

  useEffect(() => {
    exitEditRef.current = exitEdit;
  }, [exitEdit]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    let cancelled = false;
    let blurTimeoutId = 0;

    const onPointerDown = (event: PointerEvent) => {
      if (exitingRef.current) {
        return;
      }
      const cell = cellRef.current;
      const hit = event.target;
      if (!(hit instanceof Node)) {
        return;
      }
      const editor = readInlineEditorElement(block.id);
      if (cell?.contains(hit) || editor?.contains(hit)) {
        return;
      }
      if (
        isTextToolbarTarget(hit) ||
        isInlineTextLinkPopoverTarget(hit) ||
        isInlineMergeTagAutocompleteTarget(hit) ||
        isEmailBuilderFieldMergeTagPopoverTarget(hit)
      ) {
        return;
      }
      const frame = cellRef.current?.closest('[data-level="block"]');
      if (isTextFrameChromeTarget(hit, frame)) {
        return;
      }
      exitEditRef.current(true);
    };

    const onEditorBlur = (event: FocusEvent) => {
      if (
        exitingRef.current ||
        isTextToolbarTarget(event.relatedTarget) ||
        isInlineTextLinkPopoverTarget(event.relatedTarget) ||
        isInlineMergeTagAutocompleteTarget(event.relatedTarget) ||
        isEmailBuilderFieldMergeTagPopoverTarget(event.relatedTarget)
      ) {
        return;
      }
      if (toolbarPressRef.current) {
        return;
      }
      blurTimeoutId = window.setTimeout(() => {
        if (cancelled || exitingRef.current || toolbarPressRef.current) {
          return;
        }
        const editing = getEditorStoreState().textEditingBlock;
        if (isTextBlockEditing(editing, rowId, columnId, block.id)) {
          exitEditRef.current(true);
        }
      }, 0);
    };

    const onFocusOut = (event: FocusEvent) => {
      const editor = readInlineEditorElement(block.id);
      if (editor === null) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node) || !editor.contains(target)) {
        return;
      }
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && editor.contains(relatedTarget)) {
        return;
      }
      onEditorBlur(event);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusout", onFocusOut, true);

    return () => {
      cancelled = true;
      window.clearTimeout(blurTimeoutId);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, [
    block.id,
    cellRef,
    columnId,
    exitingRef,
    isEditing,
    rowId,
    toolbarPressRef,
  ]);

  function enterEdit(at?: { x: number; y: number }) {
    if (at) {
      setEmailBuilderRichTextCaretPoint(block.id, at);
    }
    setSelection({ kind: "block", ...target });
    setTextEditingBlock(target);
  }

  return { enterEdit };
}
